import { AutoComplete, type AutoCompleteProps } from 'antd';
import { useId, useMemo, useState } from 'react';
import cn from 'classnames';
import styles from './auto-complete.module.scss';

type AppAutoCompleteProps = AutoCompleteProps & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
    required?: boolean;
};

export const AppAutoComplete = ({
    label,
    value,
    defaultValue,
    onFocus,
    onBlur,
    onOpenChange,
    onDropdownVisibleChange,
    requiredMark,
    footnoteMark,
    className,
    disabled,
    popupClassName,
    rootClassName,
    required,
    ...rest
}: AppAutoCompleteProps) => {
    const id = useId();
    const [focused, setFocused] = useState(false);

    const hasValue = useMemo(() => {
        const val = value ?? defaultValue;
        if (Array.isArray(val)) return val.length > 0;
        if (val && typeof val === 'object' && 'value' in (val as any))
            return true;
        if (val === 0) return true;
        return val !== undefined && String(val ?? '').trim().length > 0;
    }, [value, defaultValue]);

    const isOccupied = focused || hasValue;

    const handleOpenChange = (visible: boolean) => {
        onOpenChange?.(visible);
        onDropdownVisibleChange?.(visible);
    };

    return (
        <div
            className={cn(styles.root, className, {
                [styles.isFocused]: focused || hasValue,
                [styles.hasValue]: hasValue,
                [styles.isDisabled]: disabled,
            })}
        >
            <AutoComplete
                id={rest.id ?? id}
                {...rest}
                value={value}
                defaultValue={defaultValue}
                disabled={disabled}
                bordered={false}
                rootClassName={cn(styles.control, rootClassName)}
                popupClassName={cn(styles.dropdown, popupClassName)}
                onFocus={event => {
                    setFocused(true);
                    onFocus?.(event);
                }}
                onBlur={event => {
                    setFocused(false);
                    onBlur?.(event);
                }}
                onOpenChange={handleOpenChange}
                placeholder={focused ? rest.placeholder : null}
            />

            {label && (
                <label
                    htmlFor={rest.id ?? id}
                    className={cn(
                        styles.label,
                        isOccupied ? styles.asLabel : styles.asPlaceholder
                    )}
                >
                    {label}
                    {(required || requiredMark) && (
                        <span className={styles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={styles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
};

AppAutoComplete.displayName = 'AppAutoComplete';

export default AppAutoComplete;
